// Local-only prototype data model. Nothing in this file establishes verified identity,
// evidence, founder tenure, business ownership, eligibility or financial entitlement.
export const STORAGE_KEY = 'growth_founder_demo_v1';
export const VERSION = 2;
export const MAX_IMPORT_BYTES = 512 * 1024;
export const MAX_ENTRIES = 300;
export const MAX_JSON_BYTES = 1024 * 1024;
export const TEMPLATE = [
  { id: 'discover', title: 'Describe the problem clearly', detail: 'Identify the problem you want to solve and who experiences it.', period: 'Months 1–3', done: false },
  { id: 'interviews', title: 'Talk to potential customers', detail: 'Conduct honest discovery conversations without pitching the answer.', period: 'Months 1–3', done: false },
  { id: 'alternatives', title: 'Research existing alternatives', detail: 'Record what people use today and how your idea differs.', period: 'Months 1–3', done: false },
  { id: 'prototype', title: 'Make a first prototype', detail: 'Use a sketch, mock-up, service trial or working product.', period: 'Months 4–6', done: false },
  { id: 'test', title: 'Test and collect feedback', detail: 'Find what works, what does not and what should change.', period: 'Months 4–6', done: false },
  { id: 'costs', title: 'Understand the costs', detail: 'Estimate delivery, marketing, operating costs and pricing assumptions.', period: 'Months 7–9', done: false },
  { id: 'iterate', title: 'Improve based on evidence', detail: 'Show what you changed after real research and testing.', period: 'Months 7–9', done: false },
  { id: 'plan', title: 'Prepare a responsible business plan', detail: 'Summarise the model, risks, validation and next resources needed.', period: 'Months 10–12', done: false },
  { id: 'pitch', title: 'Prepare your pitch and evidence', detail: 'Present what you learned, built and can substantiate.', period: 'Months 10–12', done: false },
];
export const CATEGORIES = ['Customer research','Prototype','Experiment','Learning','Costs and operations','Business plan','Other'];
export const STAGES = ['Just an idea', 'Doing research', 'Building a prototype', 'Testing with people', 'Early customers'];

const isRecord = x => x && typeof x === 'object' && !Array.isArray(x);
const clip = (x, n) => typeof x === 'string' ? x.trim().slice(0, n) : '';
const validDate = x => typeof x === 'string' && !Number.isNaN(Date.parse(x)) && /^\d{4}-\d\d-\d\dT/.test(x);
export const emptyState = () => ({ version: VERSION, project: null, tasks: TEMPLATE.map(t => ({ ...t })), journal: [] });

export function safePublicUrl(text) {
  const candidate = clip(text, 500);
  if (!candidate) return '';
  try {
    const url = new URL(candidate);
    if (url.protocol !== 'https:' || !url.hostname || url.username || url.password) return null;
    if (/\s/.test(candidate)) return null;
    return url.toString();
  } catch (_) { return null; }
}


// Same requirements as normalizeProject; kept as a pure helper for accessible
// UI validation and independent tests. No account or verification is implied.
export function onboardingErrors(form, accepted = false) {
  const read = key => typeof form?.[key] === 'string' ? form[key].trim().length : 0;
  const errors = {};
  if (read('name') < 3) errors.name = 'Enter an idea name of at least 3 characters.';
  if (read('problem') < 15) errors.problem = 'Describe the problem in at least 15 characters.';
  if (read('audience') < 5) errors.audience = 'Describe who faces the problem in at least 5 characters.';
  if (read('solution') < 15) errors.solution = 'Describe your proposed solution in at least 15 characters.';
  if (!accepted) errors.accepted = 'Please confirm that this is a local demonstration before continuing.';
  return errors;
}

export function normalizeProject(value) {
  if (!isRecord(value)) throw new Error('Project data is missing.');
  const project = {
    founder: clip(value.founder, 80),
    name: clip(value.name, 100),
    problem: clip(value.problem, 500),
    audience: clip(value.audience, 250),
    solution: clip(value.solution, 500),
    stage: STAGES.includes(value.stage) ? value.stage : STAGES[0],
    needs: clip(value.needs, 500),
    startedAt: value.startedAt,
  };
  if (project.name.length < 3 || project.problem.length < 15 || project.audience.length < 5 || project.solution.length < 15) {
    throw new Error('Project name, problem, audience or solution is incomplete.');
  }
  if (!validDate(project.startedAt)) throw new Error('Project start date is invalid.');
  return project;
}

function normalizeTask(input, template) {
  const done = input?.done === true;
  return { ...template, done, completedAt: done && validDate(input?.completedAt) ? input.completedAt : null };
}

function normalizeEntry(entry) {
  if (!isRecord(entry)) throw new Error('Invalid journal record.');
  const parsed = {
    id: clip(entry.id, 90), title: clip(entry.title, 120),
    category: CATEGORIES.includes(entry.category) ? entry.category : 'Other',
    action: clip(entry.action, 1000), result: clip(entry.result, 1000),
    next: clip(entry.next, 1000), reference: safePublicUrl(entry.reference),
    taskId: TEMPLATE.some(t => t.id === entry.taskId) ? entry.taskId : '',
    createdAt: entry.createdAt, updatedAt: validDate(entry.updatedAt) ? entry.updatedAt : null,
    verification: 'self-reported',
  };
  if (!parsed.id || !validDate(parsed.createdAt) || parsed.title.length < 3 || parsed.action.length < 10 || parsed.result.length < 10 || parsed.reference === null) {
    throw new Error('A journal entry is incomplete, dated incorrectly, or has an unsafe URL.');
  }
  return parsed;
}

export function normalizeState(raw) {
  if (!isRecord(raw) || ![1, VERSION].includes(raw.version)) throw new Error('Unsupported or invalid GROWTH file version.');
  if (!Array.isArray(raw.tasks) || !Array.isArray(raw.journal)) throw new Error('Tasks or journal are missing.');
  if (raw.journal.length > MAX_ENTRIES) throw new Error(`Maximum ${MAX_ENTRIES} journal entries supported in this local prototype.`);
  const project = raw.project === null ? null : normalizeProject(raw.project);
  if (!project && raw.journal.length) throw new Error('Journal entries have no project.');
  const existingTasks = new Map(raw.tasks.filter(isRecord).map(t => [t.id, t]));
  const tasks = TEMPLATE.map(t => normalizeTask(existingTasks.get(t.id), t));
  const journal = raw.journal.map(normalizeEntry);
  if (new Set(journal.map(x => x.id)).size !== journal.length) throw new Error('Duplicate journal IDs in backup.');
  return { version: VERSION, project, tasks, journal };
}

export function parseBackup(content) {
  if (typeof content !== 'string' || new TextEncoder().encode(content).length > MAX_IMPORT_BYTES) throw new Error('Backup is too large.');
  let value;
  try { value = JSON.parse(content); } catch (_) { throw new Error('Backup is not valid JSON.'); }
  if (!isRecord(value) || !['growth-founder-local-demo-v1','growth-founder-local-demo-v2'].includes(value.format)) {
    throw new Error('This is not a recognised GROWTH Founder backup.');
  }
  // The original Phase 1 exporter omitted `version` even though browser state included it.
  // Accept that exact legacy shape without weakening validation of other backups.
  if (value.format === 'growth-founder-local-demo-v1' && value.version === undefined) value.version = 1;
  return normalizeState(value);
}

export function serializeBackup(data) {
  return JSON.stringify({ format: 'growth-founder-local-demo-v2', exportedAt: new Date().toISOString(), ...normalizeState(data) }, null, 2);
}

export function validateEntryInput(input) {
  if (!isRecord(input)) throw new Error('Entry is missing.');
  const title = clip(input.title, 120), action = clip(input.action, 1000), result = clip(input.result, 1000);
  if (title.length < 3 || action.length < 10 || result.length < 10) throw new Error('Enter a title, what you did and what happened.');
  const reference = safePublicUrl(input.reference);
  if (reference === null) throw new Error('Reference must be a valid public HTTPS link without embedded credentials.');
  return {
    title, action, result, reference,
    next: clip(input.next, 1000),
    category: CATEGORIES.includes(input.category) ? input.category : 'Other',
    taskId: TEMPLATE.some(t => t.id === input.taskId) ? input.taskId : '',
  };
}
